/**
 * Sector roll-up: cap-weighted return, total mcap, breadth KPIs (F9, plan §3.3).
 *
 * Two shapes on purpose. `SectorAggregateDetail` is what `core` computes and
 * carries `avgVol` plus the member rows the sector screen and the treemap meta
 * line need; `toSectorAggregate` narrows it to the wire schema. The wire type is
 * change-controlled (ADR-0005), so a UI affordance that needs one more number
 * does not get to widen the contract by accident — it stays core-internal until
 * an ADR promotes it.
 *
 * Ordering is cap-weighted return DESCENDING, which is also the treemap's
 * incoming order; `squarify` re-sorts by value, so the two orders are
 * independent and neither is load-bearing for the other.
 */
import type { SectorAggregate, SectorId, WindowKey } from '@meridian/contracts';

import { SECN } from '../constants/sectors';

import { aggregateGroups } from './group-aggregate';

export interface SectorMember {
  readonly sc: SectorId;
  readonly mcap: number | null;
  readonly vol: number | null;
  readonly ret: Readonly<Record<WindowKey, number | null>>;
}

export interface SectorAggregateDetail<T extends SectorMember = SectorMember> {
  readonly sc: SectorId;
  readonly name: string;
  readonly mcap: number;
  /** `null` when no member carried weight — never coalesced to 0 (ADR-0015). */
  readonly ret: number | null;
  readonly memberCount: number;
  readonly advancers: number;
  readonly decliners: number;
  /** Mean annualised vol across members that report one. Core-only. */
  readonly avgVol: number;
  /** The member rows, in input order. Core-only. */
  readonly members: readonly T[];
}

export function aggregateSectors<T extends SectorMember>(
  rows: readonly T[],
  window: WindowKey,
): readonly SectorAggregateDetail<T>[] {
  return aggregateGroups(rows, {
    idOf: (r) => r.sc,
    nameOf: (id) => SECN[id],
    window,
  }).map((group) => ({
    sc: group.id,
    name: group.name,
    mcap: group.mcap,
    ret: group.ret,
    memberCount: group.memberCount,
    advancers: group.advancers,
    decliners: group.decliners,
    avgVol: group.avgVol,
    members: group.members,
  }));
}

/** Narrow the core detail to the change-controlled wire shape. */
export function toSectorAggregate(detail: SectorAggregateDetail): SectorAggregate {
  return {
    sc: detail.sc,
    name: detail.name,
    mcap: detail.mcap,
    ret: detail.ret,
    memberCount: detail.memberCount,
    advancers: detail.advancers,
    decliners: detail.decliners,
  };
}
